/*
AKA "strategy map wob" in Scoreboard 2
*/
import _ from 'lodash'
import * as colors from 'scoreboard-colors'

import i18n from 'scoreboard/components/i18n'
import WidgetModel from '../widget-model'
import ShapeView from './view/shape-view'
import { shortenedAndUnshortened } from 'scoreboard/components/format-value'
import calendars from 'scoreboard/components/calendars'
import trend from 'scoreboard/components/trend'
import config from 'scoreboard/components/config'
import momentHelper from 'scoreboard/components/moment-helper'
import DataType, { DATASET_LINK } from 'scoreboard/datasets/model/data-type'
const { THRESHOLD_ID, NUMBER, PERCENT} = DataType
import resolveWidgetPeriod, {
	extendCalendarError
} from '../resolve-widget-period'
import formatValue from 'scoreboard/components/format-value'
import datasetValuePrecision from 'scoreboard/datasets/dataset-value-precision'
import Dataset from 'scoreboard/datasets/model/dataset'
import DataSourceWidgetModel, { NO_DATA_COLOR } from '../data-source-widget-model'
import ScorecardNode from 'scoreboard/scorecards/scorecard-node'
const { metricish } = ScorecardNode
import { bubblePropertyNames } from './sort-show-properties'
import { BACKGROUND_PADDING_HEIGHT, BACKGROUND_PADDING_WIDTH, LOCKED_AR_SHAPES } from './view/shape-view'
import ThresholdColorsModel from 'scoreboard/components/threshold-colors/threshold-colors-model'

const { DESCRIPTION, COLOR } = bubblePropertyNames

import showPropertyToTextProperty from './show-property-to-text-property'
import textPropertyToShowProperty from './text-property-to-show-property'
import {
	SPECIFIC_TEXT,
	DATA_SOURCE,
	NONE,
	SPECIFIC_FIELD
} from './shape-text-type'
import {
	SHAPE_TEXT_TYPE,
	SHAPE_SPECIFIC_TEXT,
	SHAPE_TEXT_PROPERTY,
	SHAPE_COLOR_TYPE,
	SHAPE_SPECIFIC_COLOR,
	DEFAULT_SHAPE_COLOR,
	SHAPE_TEXT_BOLD,
	SHAPE_TEXT_ITALIC,
	SHAPE_TEXT_UNDERLINE,
	HANDLES,
	SHAPE_TEXT_ROTATION,
	SHAPE_OPACITY,
	TIP_SIZE,
	SHAPE,
	SHAPE_HEIGHT,
	DATA_SOURCE_PROPERTY
} from './shape-widget-properties'
import ColorPicker from 'scoreboard/components/color-picker'
const { AUTO } = ColorPicker
import { CONDITIONAL, PERFORMANCE, SPECIFIC_COLOR } from './shape-color-type'
import DomainType from 'scoreboard/components/domain-type'
const { SCORECARD_ITEM, INITIATIVE } = DomainType
import defaultShapeDimensions from './default-shape-dimensions'
import constants from 'scoreboard/components/constants'
import getAggregatedDataType from '../../../components/get-aggregated-data-type'
import formatHtmlValue from 'scoreboard/components/format-html-value'
import {
	getValueForThresholdColoring
} from 'scoreboard/components/thresholds-and-colors'
const { UNIQUE } = constants

function minDimension (backgroundPadding) {
	let minShapeDim = this.getMinShapeDimension()

	// #18752 If there's no background, let the shape widget get as small as
	// the shape SVG itself (usually 10x10)
	// If there is a background, though, there's padding around each side, so
	// add that in so the shape's minimum's are still respected
	return this.properties.get('background') === 'none'
		? minShapeDim
		: minShapeDim + backgroundPadding
}

var ShapeModel = DataSourceWidgetModel.extend({
	viewCls: ShapeView,

	rotatable: true,
	canHaveOutline: true,
	showPropsByDomainType,

	// If these change, update WIDGET_DIMENSIONS in DashboardBuilder.kt
	containerDefaults: {
		width: 150,
		height: 90,
		// Note: because of `this` binding, minWidth and minHeight are defined
		// in initialize()
	},

	initialize: function (props, opts = {}) {
		// Restrict performance bubble connectors to just the strategy maps section Issue #15313
		if (opts.section === 'strategyMaps') {
			this.connectable = true
		}


		this.containerDefaults.minWidth = minDimension.bind(this,
			BACKGROUND_PADDING_WIDTH)
		// Need to add 1px for the border (not sure why this isn't needed for
		// the width)
		this.containerDefaults.minHeight = minDimension.bind(this,
			BACKGROUND_PADDING_HEIGHT + 2)

		WidgetModel.prototype.initialize.apply(this, arguments)
	},

	isInteractive: function () {
		return false
	},

	/**
	 * Returns shape and domain object data needed for rendering the bubble
	 *
	 * @param {DatasetQueryValueResponse} [datasetQueryResponse] - if provided,
	 * the results of the dataset query for this widget. If not provided, and
	 * this has a dataset datasource, then it is assumed the query has not
	 * completed yet, and only basic shape-rendering data will be returned
	 */
	relevantData: function ({ datasetQueryResponse } = {}) {
		var props = this.properties.toJSON()

		let periodId = resolveWidgetPeriod(this)
		let error = periodId ? null : extendCalendarError(props.calendarId)

		var section = this.section()
		var id = props.domainObjectId + ''
		var domainObject = this.domainObject() || {}

		var relevant = _.extend({}, domainObject)
		relevant.properties = props

		if (section === 'scorecards') {
			if (domainObject) {
				var sn = domainObject
				var currentScore = sn.scores && sn.scores[periodId]

				_.extend(relevant, this.formattedScoreInfo(sn, periodId))

				if (periodId) {
					var previousPeriod = calendars.relativePeriod(periodId, -1)
					if (previousPeriod) {
						var previousScore = sn.scores && sn.scores[previousPeriod.id]
						if (previousScore) {
							// score is displayed using the database's default precision, see above
							var score = shortenedAndUnshortened({value: previousScore.score})
							relevant.previous = {
								score: score.shortened,
								scoreUnshortened: score.unshortened,
								color: previousScore.color
							}
						}
					}
				}
			}

			relevant.trend = trend(sn, previousScore, currentScore)
		} else if (section === 'initiatives') {
			relevant.performanceColor = this.getInitiativeColor()
		}


		if (domainObject) {
			relevant.organization = this.data.organizations(domainObject.organizationId)
		}

		var allPossibleFields = this.flatShowProperties(periodId)
		relevant.legendFields = allPossibleFields.filter(f => {
			if (f.property === DESCRIPTION) {
				// #20665 if the domain object has no description, treat it like
				// showDescription=false so that we don't get an empty placeholder
				// for it in the DOM. Note that we only need to do this for description,
				// because the other legend fields are labeled and it's okay for them
				// to show a blank value
				if (_.isEmpty(f.value) || _.isEmpty(f.value.trim())) {
					return false
				}
			}

			return f.legend && props[f.property]
		})

		_.extend(relevant, this.getShapeText(
			{ allPossibleFields, datasetQueryResponse }))

		this.applyShapeColor(
			{ periodId, datasetQueryResponse, relevant })

		relevant.hoverText = this.hoverText({
			domainObjectId: id,
			displayedText: relevant.shapeText
		})

		_.extend(relevant, this.getStrokeProperties())

		relevant.handles = this.handles()
		relevant.tipSize = _.isNil(props.tipSize) ? null : parseFloat(props.tipSize)
		relevant.opacity = _.isNil(props[SHAPE_OPACITY])
			? 1
			: (parseFloat(props[SHAPE_OPACITY]) / 100)

		// For explanation of forcing 0 when shape is invisible, see:
		// https://github.com/SpiderStrategies/Scoreboard/issues/17619#issuecomment-2007859976
		relevant.shapeTextRotation = this.isShapeVisible()
			? props[SHAPE_TEXT_ROTATION] : 0

		// #62794 if the shape is invisible, force it to be a 'rectangle' so
		// that the trend arrow always shows up in the same place (the far
		// top-left corner)
		relevant.symbol = this.isShapeVisible() ? props[SHAPE] : 'rectangle'

		relevant.error = error

		return relevant
	},

	getStrokeProperties () {
		let wobOutline = this.wob.get('outlineWidth')
		let strokeWidth = _.isNil(wobOutline) ? 0 : parseInt(wobOutline)

		let stroke = this.wob.get('outlineColor')
		if (stroke === AUTO) {
			// #56997 To get the "automatic" color from impact-shape, pass it `null`
			stroke = null
		}

		return {
			strokeWidth,
			stroke: stroke == null ? null : colors.lookup(stroke)?.color
		}
	},

	/**
	 * Returns the shapeText and abbreviatedShapeText values for "relevantData"
	 *
	 * @param allPossibleFields - the result of this.flatShowProperties() (passed
	 * in as an input so it only needs to be built once)
	 *
	 * @param []datasetQueryResponse] - see relevantData()
	 *
	 * @return {{shapeText: String, abbreviatedShapeText?: String}|null}
	 */
	getShapeText ({ allPossibleFields, datasetQueryResponse }) {
		let textType = this.properties.get(SHAPE_TEXT_TYPE)

		if (textType === NONE) {
			return null
		}

		if (textType === SPECIFIC_TEXT) {
			return {
				shapeText: _.escape(this.properties.get(SHAPE_SPECIFIC_TEXT))
			}
		}

		// If we're here, it's DATA_SOURCE or SPECIFIC_FIELD
		if (this.isDatasetSource()) {
			// The only thing dataset sources can return is the result of the
			// query, so just use that
			return {
				// formatHtmlValue() already escapes the results, so don't
				// double-escape here
				shapeText: this.formattedDatasetQueryResult(
					{ datasetQueryResponse })
			}
		}

		let textProperty = this.properties.get(textType === SPECIFIC_FIELD
			// A specific property was chose for shape text, so use that
			? SHAPE_TEXT_PROPERTY
			// We're using the "main" data source field, so use that
			: DATA_SOURCE_PROPERTY)

		let asShowProperty = textPropertyToShowProperty(textProperty)
		let field = allPossibleFields.find(f => f.property === asShowProperty)
		let value = field?.value

		let shapeText = null
		let abbreviatedShapeText = null
		if (!_.isNil(value)) {
			shapeText = value

			// #28844 the "Color" option has special handling for abbreviating/
			// the shape text (instead of truncating)
			if (asShowProperty === COLOR) {
				abbreviatedShapeText = abbreviatedColor(value)
			}
		}

		return {
			shapeText: _.escape(shapeText),
			abbreviatedShapeText: _.escape(abbreviatedShapeText)
		}
	},

	/**
	 * Returns the formatted results of the dataset query
	 *
	 * @param {DatasetQueryValueResponse} datasetQueryResponse
	 */
	formattedDatasetQueryResult ({ datasetQueryResponse }) {
		let fieldId = this.properties.get('fieldId')
		let field = this.dataset().getField(fieldId)

		let dataType = this.getDatasetSourceDataType()

		return formatHtmlValue({
			dataset: this.dataset(),
			fieldId,
			treeItemsById: _.keyBy(datasetQueryResponse?.treeItems, 'id'),
			value: datasetQueryResponse?.value,
			precision: datasetValuePrecision({
				field,
				configuredPrecision: this.properties.get('precision')
			}),
			dataType,
			currencyCode: field?.currencyCode,
			shorten: this.properties.get('showAbbreviatedNumbers'),
			timeFormat: momentHelper.getTimeFormat(field?.timeDisplayType)
		})
	},

	/**
	 * Returns the data type that should be used for the dataset source
	 * @return {DataType}
	 */
	getDatasetSourceDataType () {
		let { fieldId, aggregationType, dataSourceType } =
			this.get('properties')
		let field = this.dataset().getField(fieldId)

		// #51615 use this logic instead of components/get-aggregated-data-type.js
		if (dataSourceType == 'calculated') {
			return this.properties.get('dataType')
		} else {
			// #62923 dataType isn't actually configured for BASIC sources;
			// start out with the field's dataType
			let dataType = field?.dataType

			// #59880 be sure to grab the dataType from the underlyingField in case
			// the field was of dataType 'linked-field'
			let underlyingField = this.dataset().getUnderlyingField(field)
			if (dataType == DATASET_LINK) {
				dataType = underlyingField?.dataType
			}

			if (Dataset.isTotal(fieldId)) {
				dataType = NUMBER
			} else if (aggregationType)  {
				// #56746
				// If an aggregation type exists, we should define the data type
				// based on it, not the field's data type

				// #59817 we do still need to pass in the field's dataType,
				// though, if none was specified in the data source
				dataType = getAggregatedDataType(
					dataType ?? underlyingField?.dataType,
					aggregationType)
			} else {
				dataType = underlyingField?.dataType
			}

			return dataType
		}
	},

	formattedScoreInfo: function (sn, periodId) {
		var info = {}
		var currentScore = sn.scores && sn.scores[periodId]
		let currentPctWeighting = sn.percentWeightings && sn.percentWeightings[periodId]

		if (!_.isNil(currentPctWeighting)) {
			var percent = shortenedAndUnshortened({ value: currentPctWeighting, dataType: 'percent' })
			info.percentWeighting = percent.shortened
			info.percentWeightingUnshortened = percent.unshortened
		}

		if (currentScore) {
			info.performanceColor = currentScore.color

			if (currentScore.applicable) {
				var score = shortenedAndUnshortened({
					value: currentScore.score,
					dataType: 'score'
				})
				info.score = score.shortened
				info.scoreUnshortened = score.unshortened
				// note that the score is displayed using the database's default precision,
				// and is not overridden by the metric's precision setting.
				// see https://github.com/SpiderStrategies/Scoreboard/issues/5284
			} else {
				info.score = i18n.get('scorecards.node.metric.notapplicable')
			}

			if (metricish(sn)) {
				if (currentScore.applicable) {
					let applyMetricInfo = (prefix, raw) => {
						let formatted = shortenedAndUnshortened({
							value: raw,
							dataType: currentScore.dataType || sn.dataType,
							precision: this.getNumericProperty('precision')
								?? sn.precision,
							currencyCode: sn.currencyCode
						})

						info[prefix] = this.properties.get('showAbbreviatedNumbers')
							? formatted.shortened : formatted.unshortened
						info[prefix + 'Unshortened'] = formatted.unshortened
					}

					// Add in the metric's formatted value
					applyMetricInfo('value', currentScore.actual)

					// If the metric has a goal threshold, add it in
					let rawGoal = this.getGoalThreshold(currentScore)
					if (!_.isNil(rawGoal)) {
						applyMetricInfo('goal', rawGoal)
					}
				} else {
					info.value = info.goal = i18n.get('scorecards.node.metric.notapplicable')
				}
			}
		} else {
			info.performanceColor = NO_DATA_COLOR
		}

		return info
	},

	/**
	 * Returns the threshold value for the goal threshold, or null if there
	 * is no goal threshold.
	 */
	getGoalThreshold (score) {
		let sn = this.domainObject()
		if (!sn) {
			return null
		}

		let scoringType = config.scoringType(sn.scoringTypeId)
		if (_.isNumber(scoringType.goalIndex)) {
			if (sn.dataType == THRESHOLD_ID) {
				return scoringType.thresholds[scoringType.goalIndex].id
			} else {
				return score.thresholds[scoringType.goalIndex]
			}
		}
	},

	applyShapeColor ({ periodId, datasetQueryResponse, relevant }) {
		let type = this.properties.get(SHAPE_COLOR_TYPE)

		let hexColor
		if (type === CONDITIONAL) {
			hexColor = this.getConditionalColor(
				{ periodId, datasetQueryResponse})
		} else {
			let shapeColor
			if (type === PERFORMANCE) {
				shapeColor = relevant.performanceColor ?? NO_DATA_COLOR
			} else if (type === SPECIFIC_COLOR) {
				shapeColor = this.properties.get('shapeSpecificColor')
			} else {
				shapeColor = NO_DATA_COLOR
			}

			hexColor = colors.lookup(shapeColor).color
		}

		if (this.properties.get('fill') === NONE) {
			// #17619 If there's no actual shape being displayed, the "shapeColor"
			// is used instead for the shape text color
			relevant.shapeTextColor = hexColor
		} else {
			relevant.color = hexColor
		}
	},

	getConditionalColor ({ periodId, datasetQueryResponse}) {
		let colorModel = new ThresholdColorsModel(
			this.wob.get('thresholdColoring'))

		let value = this.getDataSourceValue({ periodId, datasetQueryResponse })
		const dataType = this.getDataTypeForConditionalColoring()
		value = getValueForThresholdColoring(value, dataType)

		if (_.isNil(value) || !_.isFinite(value)) {
			return colors.lookup(NO_DATA_COLOR).color
		} else {
			// Reports use full opacity (ie. 1), so that seems reasonable here
			return colorModel.color(value, 1)?.formatHex()
		}
	},

	/**
	 * Returns the data type that should be used for conditional coloring
	 * (this is used to determine if the value represents a percentage and should
	 * have the decimal moved)
	 */
	getDataTypeForConditionalColoring () {
		let dataType
		if (this.isDatasetSource()) {
			// for dataset sources, the data type is set in the properties
			dataType = this.properties.get('dataType')
			let aggregationType = this.properties.get('aggregationType')
			// if the aggregation type is 'unique', the data type should be
			// considered 'number'
			if (aggregationType == UNIQUE) {
				dataType = NUMBER
			}
		} else {
			let domainType = this.domainType()
			if (domainType == SCORECARD_ITEM) {
				// for scorecard items, the data type is found on the domain object
				dataType = this.domainObject()?.dataType
			}
			if (domainType == INITIATIVE) {
				// for initiative items, find the calculation type for the
				// data source property (ex. 'percent-complete') and get the
				// dataType from that
				let dataSourceProperty = this.properties.get(DATA_SOURCE_PROPERTY)
				dataType = constants.initiativeCalculationTypes[dataSourceProperty]?.dataType
			}
		}
		return dataType
	},

	getDataSourceValue ({ periodId, datasetQueryResponse }) {
		if (this.isDatasetSource()) {
			// The "data source" for a dataset is always the result of the query
			return datasetQueryResponse?.value
		} else {
			// Scorcard and initiative data sources use a specific property for
			// their data source (eg "score", "actual", "budget-spent")
			let dataSourceProperty = this.properties.get(DATA_SOURCE_PROPERTY)
			let domainType = this.domainType()

			if (domainType === SCORECARD_ITEM) {
				return this.getScorecardDataSourceValue(
					{ dataSourceProperty, periodId })
			} else if (domainType === INITIATIVE) {
				let calcs = this.domainObject()?.calculations?.[periodId]?.calculations
				return calcs?.[dataSourceProperty]
			}
		}
	},

	getScorecardDataSourceValue ({ dataSourceProperty, periodId }) {
		let score = this.domainObject()?.scores[periodId]
		switch (dataSourceProperty) {
			case 'score':
				return score?.score
			case 'actual':
				return score?.actual
			case 'goal':
				return this.getGoalThreshold(score)
			case 'weight':
				return this.domainObject()?.percentWeightings?.[periodId]
		}
	},

	/**
	 * Sets the background for the bubble and makes any necessary adjustments
	 * to the shapeHeight
	 *
	 * @override
	 */
	setBackground (newBackground) {
		let { update, turningOnBackground } =
			this.getBackgroundUpdate(newBackground)

		// #17619 "When changing the widget Background from None to Solid or
		// Translucent, the Shape Opacity should change to 100%"
		if (turningOnBackground) {
			update[SHAPE_OPACITY] = 100
		}

		this.properties.set(update)
	},

	/**
	 * Returns an appropriate object for updating the widget's background
	 * via this.properties.set(). This does not actually update the model; it
	 * is split into its own method so that other methods can update the
	 * background in a the same action (eg when changing opacity)
	 */
	getBackgroundUpdate (newBackground) {
		let oldBackground = this.properties.get('background')
		let shapeHeight = parseInt(this.properties.get('shapeHeight'))

		let update = {
			background: newBackground
		}

		let turningOnBackground =
			oldBackground === NONE && newBackground !== NONE
		let turningOffBackground =
			oldBackground !== NONE && newBackground === NONE

		let heightAdjustment = 0
		if (turningOffBackground) {
			if (this.isShapeVisible()) {
				// We had a background ('white' or 'translucent') and now have
				// none, so expand the bubble to fill the padding that was there
				// before (not necessary if there's no fill)
				heightAdjustment += BACKGROUND_PADDING_HEIGHT
			}
			// Adjust for the 1px border around each side
			heightAdjustment += 2
		} else if (turningOnBackground) {
			if (this.isShapeVisible()) {
				// We didn't have have a background and now we do, so shrink the
				// bubble to add some padding inside the background (not
				// necessary if there's no fill)
				heightAdjustment -= BACKGROUND_PADDING_HEIGHT
			}
			// Adjust for the 1px border around each side
			heightAdjustment -= 2
		}

		if (heightAdjustment !== 0) {
			update.shapeHeight = min0(shapeHeight + heightAdjustment)
		}

		return { update, turningOnBackground }
	},

	/**
	 * Sets the shape's opacity and makes any other necessary adjustments
	 * (such as turning off the background)
	 *
	 * @param {Integer} opacity - the new opacity value
	 */
	setOpacity (opacity) {
		let update = { [SHAPE_OPACITY]: opacity }

		// #17619 "When changing the Shape Opacity to anything less than 100%,
		// the widget Background should change to None."
		if (opacity < 100) {
			let bgUpdate = this.getBackgroundUpdate(NONE)
			_.extend(update, bgUpdate.update)
		}

		this.properties.set(update)
	},

	setShape (shape) {
		if (this.properties.get('shape') === shape) {
			return
		}

		let update = {
			[SHAPE]: shape,
			// Clear out old handles/tipSize so that the new shape's defaults
			// are used
			[HANDLES]: null,
			[TIP_SIZE]: null
		}

		// If we're switching to a shape with a locked aspect ratio, we need to
		// adjust the shapeHeight to match the new shape's aspect ratio
		if (LOCKED_AR_SHAPES.includes(shape)) {
			let newShapeDefaults = defaultShapeDimensions[shape]
			let defaultHeight = newShapeDefaults.height
				?? this.containerDefaults.height
			let defaultWidth = newShapeDefaults.width
				?? this.containerDefaults.width
			let aspectRatio = defaultHeight / defaultWidth

			let currentShapeWidth = this.shapeDimensions().width
			let newShapeHeight = Math.round(currentShapeWidth * aspectRatio)
			update[SHAPE_HEIGHT] = newShapeHeight
		}

		this.properties.set(update)
	},

	shapeDimensions () {
		let container = this.wobDimensions()
		let height = parseFloat(this.properties.get(SHAPE_HEIGHT))

		let width
		if (this.properties.get('background') === NONE) {
			// When there's no background, the shape should use the full width
			// of the wob
			width = min0(container.width)
		} else {
			// When there's a background need to shrink the shape width to allow
			// for padding
			width = min0(container.width - BACKGROUND_PADDING_WIDTH)
		}

		return {
			height,
			width,
			rotate: this.wob.get('rotate')
		}
	},

	/**
	 * Returns the minimum height/width allowed for the current shape type
	 * @return {number}
	 */
	getMinShapeDimension () {
		return this.properties.get('shape') === 'line' ? 1 : 10
	},

	wobDimensions () {
		return this.wob.pick('height', 'width')
	},

	setColor ({ shapeColorType, shapeSpecificColor, thresholdColoring }) {
		this.properties.set({ shapeColorType, shapeSpecificColor })

		this.wob.set('thresholdColoring', shapeColorType === CONDITIONAL
			? thresholdColoring : null)
	},

	/**
	 * Returns true if this bubble supports using performance colors (scorecards
	 * and initiatives); false if the color is always the same (all other sections)
	 */
	supportsPerformanceColors() {
		return _.includes(['scorecards', 'initiatives'], this.section())
	},

	considerProperties: function (proposedChanges) {
		var allowedChanges = _.extend({}, proposedChanges)

		this.considerShapeColorProperties(proposedChanges, allowedChanges)
		this.considerShapeTextProperties(proposedChanges, allowedChanges)

		return WidgetModel.prototype.considerProperties.call(this, allowedChanges)
	},

	considerShapeColorProperties (proposedChanges, allowedChanges) {
		if (proposedChanges.shapeColorType == PERFORMANCE
			&& (this.isDatasetSource() || this.isNoneSource())) {

			delete allowedChanges.shapeColorType
			// Make sure we don't clear out the specific color if it's already set
			delete allowedChanges.shapeSpecificColor
		}
	},

	considerShapeTextProperties (proposedChanges, allowedChanges) {
		function clearShapeTextProperties() {
			delete allowedChanges.shapeTextType
			delete allowedChanges.shapeTextProperty
			delete allowedChanges.shapeSpecificText
		}

		if (proposedChanges.shapeTextType == SPECIFIC_FIELD) {
			if (this.isNoneSource() || this.isDatasetSource()) {
				// SPECIFIC_FIELD isn't allowed for "None" or "dataset" data source,
				// so we should ignore it
				clearShapeTextProperties()
			} else {
				if (!this.isValidShapeTextProperty(proposedChanges.shapeTextProperty)) {
					clearShapeTextProperties()
				}
			}
		} else if (proposedChanges.shapeTextType == DATA_SOURCE && this.isNoneSource()) {
			// DATA_SOURCE isn't allowed for "None" data source,
			// so we should ignore it
			clearShapeTextProperties()
		}
	},

	multiSettableProps: function () {
		return _.map(this.flatShowProperties(), 'property')
			.concat([ 'shape', 'fill', 'fontSize', 'outsideFontSize', 'background',
				'drilldownId', 'drilldownSection', 'textColor', 'textAlign', 'showAbbreviatedNumbers',
				'calendarId', 'periodId', 'periodDelta', 'label', 'precision',
				SHAPE_TEXT_TYPE, SHAPE_SPECIFIC_TEXT, SHAPE_TEXT_PROPERTY,
				SHAPE_COLOR_TYPE, SHAPE_SPECIFIC_COLOR,
				SHAPE_TEXT_BOLD, SHAPE_TEXT_ITALIC, SHAPE_TEXT_UNDERLINE,
				SHAPE_TEXT_ROTATION, SHAPE_OPACITY, HANDLES, TIP_SIZE ])
	},

	/**
	 * Updates the properties of the widget to match the results of using
	 * DataSourceSelector
	 */
	setDataSource ({ domainType, dataSource, period }) {

		if (this.isNoneSource() && !_.isNil(domainType)
			&& this.properties.get(SHAPE_TEXT_TYPE) === NONE) {
			// #58635 if we're switching from "none" to a real data source,
			// and there wasn't already text in the shape, default to using
			// the data source
			this.properties.set(SHAPE_TEXT_TYPE, DATA_SOURCE)
		}

		DataSourceWidgetModel.prototype.setDataSource.apply(this, arguments)

		// Note: have to do this *after* switching the data source, so all the
		// code to determine appropriate properties is using the new data source
		this.ensureValidShapeTextProperties()
		this.ensureValidShapeColorProperties()
	},

	/**
	 * If necessary, changes properties related to shapeText to match the new
	 * data source
	 */
	ensureValidShapeTextProperties () {
		let domainType = this.domainType()
		let applyNoneForNoneSource = () => {
			this.properties.set({
				shapeTextType: NONE,
				shapeSpecificText: null,
				shapeTextProperty: null
			})
		}

		if (this.properties.get('shapeTextType') == SPECIFIC_FIELD) {
			if (_.isNil(domainType)) {
				// "None" data source doesn't have the SPECIFIC_FIELD shapeTextType option,
				// only NONE or SPECIFIC_TEXT, and we can't guess the text
				// they'd want, so switch to NONE
				applyNoneForNoneSource()
			} else if (domainType === 'dataset') {
				// Datasets never have use SPECIFIC_FIELD or shapeTextProperty
				this.properties.set({
					[SHAPE_TEXT_TYPE]: DATA_SOURCE,
					[SHAPE_TEXT_PROPERTY]: null
				})
			} else {
				if (!this.isValidShapeTextProperty(this.properties.get('shapeTextProperty'))) {
					// The current shapeTextProperty isn't valid for the new domainType,
					// so switch to the first valid option
					this.properties.set('shapeTextProperty',
						this.validShapeTextPropertyValues()[0])
				}
			}
		} else if (this.properties.get('shapeTextType') == DATA_SOURCE) {
			if (_.isNil(domainType)) {
				// "None" data source doesn't have the DATA_SOURCE shapeTextType option,
				// only NONE or SPECIFIC_TEXT, and we can't guess the text
				// they'd want, so switch to NONE
				applyNoneForNoneSource()
			} else {
				// No shapeTextProperty is chosen for DATA_SOURCE
				this.properties.unset('shapeTextProperty')
			}
		}
	},

	isValidShapeTextProperty (shapeTextProperty) {
		return _.includes(this.validShapeTextPropertyValues(), shapeTextProperty)
	},

	validShapeTextPropertyValues (shapeTextProperty) {
		return this.validShapeTextPropertyOptions()
			.map(o => o.value)
	},

	/**
	 * If necessary, changes properties related to shapeColor to match the new
	 * data source
	 */
	ensureValidShapeColorProperties () {
		let colorType = this.properties.get(SHAPE_COLOR_TYPE)
		if (_.isNil(this.domainType()) && colorType != SPECIFIC_COLOR) {
			// SPECIFIC_COLOR is the only valid option for "None" data source
			this.properties.set({
				shapeColorType: SPECIFIC_COLOR,
				shapeSpecificColor: DEFAULT_SHAPE_COLOR
			})
		} else if (this.isDatasetSource() && colorType == PERFORMANCE) {
			// PERFORMANCE isn't valid for datasets
			this.properties.set({
				shapeColorType: SPECIFIC_COLOR,
				shapeSpecificColor: DEFAULT_SHAPE_COLOR
			})
		}
	},

	/**
	 * @override
	 */
	drilldown (drilldown) {
		if (drilldown) {
			// When setting the drilldown, the standard widget logic is fine
			return WidgetModel.prototype.drilldown.apply(this, arguments)
		}

		if (!this.isDatasetSource()
			|| this.properties.get('drilldownSection') !== 'default') {

			// The default widget drilldown logic is fine for anything other
			// than the "default" setting with dataset sources
			return WidgetModel.prototype.drilldown.apply(this, arguments)
		}

		// #28088 don't allow default drilldown for calculated widgets
		if (this.properties.get('dataSourceType') === 'calculated') {
			return null
		}

		return {
			section: 'datasets',
			id: this.datasetId(),
			request: this.datasetRequest()
		}
	},

	/**
	 * Returns true if the shape is visible (ie has a fill or outline)
	 */
	isShapeVisible () {
		return this.properties.get('fill') !== NONE
			|| !_.isNil(this.wob.get('outlineWidth'))
	},

	/**
	 * Gets or sets shape handles. Converts to/from JSON string (which is how
	 * they are stored in the database)
	 */
	handles (newHandles) {
		return this._jsonProperty(HANDLES, newHandles, null)
	},

	outlineWidth (width) {
		if (this.properties.get('shape') === 'line') {
			// For lines, the shapeHeight is the same as the outlineWidth
			this.properties.set('shapeHeight', parseInt(width))
		}

		WidgetModel.prototype.outlineWidth.apply(this, arguments)
	}
})

/**
 * For the given localized color name (eg 'Red' or 'Dark Blue'), returns the
 * abbreviation to use when truncating the label (eg 'R' or 'DB')
 */
function abbreviatedColor (color) {
	if (_.isEmpty(color)) {
		return null
	}

	const colorSplit = color.split(' ')
	if (colorSplit.length > 1) {
		// if the color is multiple words, abbreviate it with the
		// first letter of each word
		return _.map(colorSplit, word =>
			word.substring(0,1)).join('')
	} else if (color === 'Gray') {
		// #38210 'Gray' should abbreviate to nothing
		return ''
	} else {
		// For single-word colors other than Gray, use just the
		// first letter
		return color.substring(0, 1)
	}
}

// ensure number is at least 0
var min0 = Math.max.bind(null, 0)

const showPropsForAllSections = [ 'showAbbreviatedNumbers', 'showPointerLabel' ]

const showPropsForScorecardsAndInitiatives = [
	'showLabel', 'showDescription', 'showColor', 'showPeriod'
]

/**
 * For each DomainType, an array of all the valid showX widget properties
 */
const showPropsByDomainType = {
	none: showPropsForAllSections,
	dataset: showPropsForAllSections,
	'scorecard-item': showPropsForAllSections
		.concat(showPropsForScorecardsAndInitiatives)
		.concat(['showScore', 'showGoal', 'showActual', 'showPeriod', 'showTrend',
			'showWeight']),
	initiative: showPropsForAllSections
		.concat(showPropsForScorecardsAndInitiatives)
		.concat(['showBudget', 'showBudgetRemaining', 'showBudgetSpent',
			'showBudgetVariance', 'showBudgetVariancePercent', 'showDaysElapsed',
			'showEndDate', 'showPercentComplete', 'showPercentTimeElapsed',
			'showProjectedBudget', 'showProjectedEndDate', 'showScheduleVariance',
			'showStartDate']),
}

_.extend(ShapeModel, {
	showPropsByDomainType,
	textPropertyToShowProperty,
	showPropertyToTextProperty,
	abbreviatedColor
})
export default ShapeModel
export { showPropsByDomainType }
export { textPropertyToShowProperty }
export { showPropertyToTextProperty }
export { abbreviatedColor }

ShapeModel.__filename__ = 'dashboards/widgets/shape/shape-model.js'
